import React, { useState } from "react";
import { Table } from "react-bootstrap";
import { formatNumber } from "pages/MatchDetail/MatchedOdds"; // Assuming this is for number formatting

interface FancyPLData {
  user_id: string;
  user_name: string;
  sport_name: string;
  match_id: string;
  match_name: string;
  market_id: string;
  market_name: string;
  winner_name: string;
  settle_date_time: string;
  casinoProvider: string | null;
  qtRoundId: string | null;
  qtClientRoundId: string | null;
  auraMarketId: string | null;
  auraRountId: string | null;
  total: number;
  commission: number;
  bet_type: string;
  user_price: number;
  amount: number;
  placed_time: string;
  match_date: string;
  p_l: number;
  details: string;
  is_back: number;
  odds: number;
  stack: string;
  createdAt: string;
}

interface FancyPLTableProps {
  fancyPLData: FancyPLData[];
  onClickRow: (match_id: string, match_name: string, sport_name: string, user_id: string) => void;
}

// Format date and time
export const formatDateTime = (dateString?: string) => {
  if (!dateString) return "-";
  const date = new Date(dateString);
  return date.toLocaleString("en-US", {
    month: "short",
    day: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
    hour12: true,
  });
};

// Sorting function
const sortData = <T extends object>(
  data: T[],
  sortKey: keyof T | "",
  sortOrder: "asc" | "desc"
): T[] => {
  if (!sortKey) return data;

  return [...data].sort((a, b) => {
    const aVal = a[sortKey];
    const bVal = b[sortKey];

    // Sorting for string values
    if (typeof aVal === "string" && typeof bVal === "string") {
      return sortOrder === "asc"
        ? aVal.localeCompare(bVal)
        : bVal.localeCompare(aVal);
    }

    // Sorting for numeric values
    const aNum = typeof aVal === "number" ? aVal : parseFloat(aVal as string);
    const bNum = typeof bVal === "number" ? bVal : parseFloat(bVal as string);

    if (isNaN(aNum) || isNaN(bNum)) return 0;

    return sortOrder === "asc" ? aNum - bNum : bNum - aNum;
  });
};

// Get sorting class for the column
const getSortClass = (key: string, sortKey: string, sortOrder: "asc" | "desc") => {
  const isActive = sortKey === key;
  const upClass = isActive && sortOrder === "asc" ? "text-dark" : "text-muted";
  const downClass =
    isActive && sortOrder === "desc" ? "text-dark" : "text-muted";

  return (
    <span className="d-flex flex-column ms-2">
      <i
        className={`bi bi-caret-up-fill ${upClass}`}
        style={{ lineHeight: 0.6, cursor: "pointer" }}
      />
      <i
        className={`bi bi-caret-down-fill ${downClass}`}
        style={{ lineHeight: 0.6, cursor: "pointer" }}
      />
    </span>
  );
};

const PlUserBetEventWise: React.FC<FancyPLTableProps> = ({
  fancyPLData,
  onClickRow,
}) => {
  // State for sorting
  const [sortKey, setSortKey] = useState<keyof FancyPLData | "">("");
  const [sortOrder, setSortOrder] = useState<"asc" | "desc">("asc");

  // Handle column sorting
  const handleSort = (key: keyof FancyPLData) => {
    if (sortKey === key) {
      setSortOrder((prev) => (prev === "asc" ? "desc" : "asc"));
    } else {
      setSortKey(key);
      setSortOrder("asc");
    }
  };

  // Get sorted data based on sortKey and sortOrder
  const sortedData = sortData(fancyPLData, sortKey, sortOrder);

  return (
    <div className="table-responsive">
      <Table striped hover responsive className="table-centered align-middle">
        <thead className="table-light">
          <tr>
            <th
              onClick={() => handleSort("sport_name")}
              style={{ cursor: "pointer" }}
            >
              <div className="d-flex justify-content-between align-items-center">
                <span>Sport Name</span>
                {getSortClass("sport_name", String(sortKey), sortOrder)}
              </div>
            </th>
            <th
              onClick={() => handleSort("match_name")}
              style={{ cursor: "pointer" }}
            >
              <div className="d-flex justify-content-between align-items-center">
                <span>Event Name</span>
                {getSortClass("match_name", String(sortKey), sortOrder)}
              </div>
            </th>
            <th
              onClick={() => handleSort("market_name")}
              style={{ cursor: "pointer" }}
            >
              <div className="d-flex justify-content-between align-items-center">
                <span>Market Name</span>
                {getSortClass("market_name", String(sortKey), sortOrder)}
              </div>
            </th>
            <th
              onClick={() => handleSort("winner_name")}
              style={{ cursor: "pointer" }}
            >
              <div className="d-flex justify-content-between align-items-center">
                <span>Winner Name</span>
                {getSortClass("winner_name", String(sortKey), sortOrder)}
              </div>
            </th>
            <th
              onClick={() => handleSort("bet_type")}
              style={{ cursor: "pointer" }}
            >
              <div className="d-flex justify-content-between align-items-center">
                <span>Bet Type</span>
                {getSortClass("bet_type", String(sortKey), sortOrder)}
              </div>
            </th>
            <th
              onClick={() => handleSort("user_price")}
              style={{ cursor: "pointer" }}
            >
              <div className="d-flex justify-content-between align-items-center">
                <span>User Price</span>
                {getSortClass("user_price", String(sortKey), sortOrder)}
              </div>
            </th>
            <th
              onClick={() => handleSort("amount")}
              style={{ cursor: "pointer" }}
            >
              <div className="d-flex justify-content-between align-items-center">
                <span>Amount</span>
                {getSortClass("amount", String(sortKey), sortOrder)}
              </div>
            </th>
            <th
              onClick={() => handleSort("p_l")}
              style={{ cursor: "pointer" }}
            >
              <div className="d-flex justify-content-between align-items-center">
                <span>PL</span>
                {getSortClass("p_l", String(sortKey), sortOrder)}
              </div>
            </th>
            <th
              onClick={() => handleSort("placed_time")}
              style={{ cursor: "pointer" }}
            >
              <div className="d-flex justify-content-between align-items-center">
                <span>Placed Time</span>
                {getSortClass("placed_time", String(sortKey), sortOrder)}
              </div>
            </th>
            <th
              onClick={() => handleSort("match_date")}
              style={{ cursor: "pointer" }}
            >
              <div className="d-flex justify-content-between align-items-center">
                <span>Match Date</span>
                {getSortClass("match_date", String(sortKey), sortOrder)}
              </div>
            </th>
            <th
              onClick={() => handleSort("details")}
              style={{ cursor: "pointer" }}
            >
              <div className="d-flex justify-content-between align-items-center">
                <span>Details</span>
                {getSortClass("details", String(sortKey), sortOrder)}
              </div>
            </th>
          </tr>
        </thead>
        <tbody>
          {sortedData.map((item, index) => (
            <tr
              key={index}
              onClick={() =>
                onClickRow(item.match_id, item.match_name, item.sport_name, item.user_id)
              }
              style={{ cursor: "pointer" }}
            >
              <td>{item.sport_name.replace(/\(\d+\)/, '')}</td>
              <td>{item.match_name || "-"}</td>
              <td>{item.market_name || "-"}</td>
              <td>{item.winner_name}</td>
              <td className={`${item.is_back === 1 ? "text-success" : "text-danger"}`}>
                {item.is_back === 1 ? "Back" : "Lay"}
              </td>
              <td>{item?.odds || "-"}</td>
              <td className={`${Number(item.stack) >= 0 ? "text-success" : "text-danger"}`}>
                {item.stack ? formatNumber(item.stack) : "-"}
              </td>
              <td className={`${item.p_l >= 0 ? "text-success" : "text-danger"}`}>
                {formatNumber(item.p_l.toFixed(2))}
              </td>
              <td>{formatDateTime(item.createdAt)}</td>
              <td>{formatDateTime(item.createdAt)}</td>
              <td>Info</td>
            </tr>
          ))}
        </tbody>
      </Table>
    </div>
  );
};

export default PlUserBetEventWise;
